import { type ApiErrorResponse, type ApiSuccessResponse } from '../types/api.js';

export interface ApiClientOptions {
  baseUrl: string;
  timeout?: number;
  maxRetries?: number;
  correlationIdHeader?: string;
}

export interface ApiClient {
  get: <T>(url: string, options?: RequestOptions) => Promise<ApiSuccessResponse<T>>;
  post: <T>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ) => Promise<ApiSuccessResponse<T>>;
  put: <T>(url: string, body?: unknown, options?: RequestOptions) => Promise<ApiSuccessResponse<T>>;
  patch: <T>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ) => Promise<ApiSuccessResponse<T>>;
  delete: <T>(url: string, options?: RequestOptions) => Promise<ApiSuccessResponse<T>>;
}

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  timeout?: number;
  idempotencyKey?: string;
}

export class ApiError extends Error {
  public retryAfterMs: number | null;

  constructor(
    public status: number,
    public response: ApiErrorResponse,
    retryAfterHeader?: string | null,
  ) {
    super(response.error.message);
    this.name = 'ApiError';
    this.retryAfterMs = parseRetryAfterHeader(retryAfterHeader ?? null);
  }
}

function parseRetryAfterHeader(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds > 0) return seconds * 1000;
  const date = Date.parse(header);
  if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  return null;
}

const IDEMPOTENT_METHODS = new Set(['GET', 'PUT', 'DELETE', 'HEAD', 'OPTIONS']);
const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);

function generateCorrelationId(): string {
  return crypto.randomUUID();
}

function generateTraceParent(): string {
  const traceId = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const spanId = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `00-${traceId}-${spanId}-01`;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const {
    baseUrl,
    timeout = 30000,
    maxRetries = 3,
    correlationIdHeader = 'X-Correlation-Id',
  } = options;

  async function request<T>(
    method: string,
    url: string,
    body?: unknown,
    requestOptions?: RequestOptions,
  ): Promise<ApiSuccessResponse<T>> {
    const fullUrl = `${baseUrl}${url}`;
    const correlationId = generateCorrelationId();
    const effectiveTimeout = requestOptions?.timeout ?? timeout;
    const canRetry = IDEMPOTENT_METHODS.has(method) || requestOptions?.idempotencyKey;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [correlationIdHeader]: correlationId,
      traceparent: generateTraceParent(),
      ...requestOptions?.headers,
    };

    if (requestOptions?.idempotencyKey) {
      headers['Idempotency-Key'] = requestOptions.idempotencyKey;
    }

    let lastError: Error | null = null;
    const attempts = canRetry ? maxRetries : 1;

    for (let attempt = 0; attempt < attempts; attempt++) {
      if (attempt > 0) {
        const retryAfter = lastError instanceof ApiError ? getRetryAfter(lastError) : null;
        const delay = retryAfter ?? Math.min(1000 * Math.pow(2, attempt - 1), 10000);
        await sleep(delay);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);

      try {
        const response = await fetch(fullUrl, {
          method,
          headers,
          body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
          credentials: 'include',
          signal: requestOptions?.signal
            ? combineSignals(requestOptions.signal, controller.signal)
            : controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const retryAfterHeader = response.headers.get('Retry-After');
          const errorBody = (await response.json().catch(() => ({
            success: false as const,
            error: {
              code: 'INTERNAL_ERROR' as const,
              message: response.statusText,
            },
            correlationId,
          }))) as ApiErrorResponse;

          if (RETRYABLE_STATUS_CODES.has(response.status) && canRetry) {
            lastError = new ApiError(response.status, errorBody, retryAfterHeader);
            continue;
          }

          throw new ApiError(response.status, errorBody, retryAfterHeader);
        }

        // 204/205 carry no body, and some handlers return an empty 200.
        // Parsing those as JSON throws, and because idempotent methods retry,
        // a successful DELETE would be re-sent and come back 404.
        if (response.status === 204 || response.status === 205) {
          return { success: true, data: undefined as T, correlationId };
        }
        const text = await response.text();
        if (text.trim() === '') {
          return { success: true, data: undefined as T, correlationId };
        }
        return JSON.parse(text) as ApiSuccessResponse<T>;
      } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof ApiError) {
          throw error;
        }

        if (error instanceof DOMException && error.name === 'AbortError') {
          if (requestOptions?.signal?.aborted) {
            throw new Error('Request cancelled');
          }
          throw new Error(`Request timeout after ${effectiveTimeout}ms`);
        }

        lastError = error instanceof Error ? error : new Error(String(error));

        if (!canRetry || attempt >= attempts - 1) {
          throw lastError;
        }
      }
    }

    throw lastError ?? new Error('Request failed');
  }

  return {
    get: <T>(url: string, opts?: RequestOptions) => request<T>('GET', url, undefined, opts),
    post: <T>(url: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('POST', url, body, opts),
    put: <T>(url: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('PUT', url, body, opts),
    patch: <T>(url: string, body?: unknown, opts?: RequestOptions) =>
      request<T>('PATCH', url, body, opts),
    delete: <T>(url: string, opts?: RequestOptions) => request<T>('DELETE', url, undefined, opts),
  };
}

function getRetryAfter(error: ApiError): number | null {
  return error.retryAfterMs;
}

function combineSignals(userSignal: AbortSignal, timeoutSignal: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  userSignal.addEventListener('abort', onAbort, { once: true });
  timeoutSignal.addEventListener('abort', onAbort, { once: true });
  return controller.signal;
}
